import React, { useMemo } from 'react';
import {
  ReportBody,
  ReportField,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportPack,
  ReportPage,
  ReportSection,
  ReportTitleRow,
} from '../../../components/ui/Report';
import { buildComplianceExamples } from '../complianceExamples';

export type WrhRulesReportSet = {
  id: number;
  name: string;
  isActive: boolean;
  minRest24h: number;
  minRest7d: number;
  maxWork24h: number;
  maxRestSplits: number;
  minLongestRestSegment: number;
  maxGapBetweenRest: number;
  notes?: string | null;
};

export type WrhRulesReportData = {
  ruleSet: WrhRulesReportSet;
  managementCompany?: {
    id: number;
    name: string;
    logoStorageKey?: string | null;
  } | null;
};

function n(v: number | string | null | undefined, fallback = 0): number {
  const x = Number(v);
  return Number.isFinite(x) ? x : fallback;
}

type RulesReportProps = {
  data: WrhRulesReportData;
  companyLogoSrc?: string | null;
};

export const RulesReport: React.FC<RulesReportProps> = ({ data, companyLogoSrc = null }) => {
  const r = data.ruleSet;
  const thresholds = useMemo(
    () => ({
      minRest24h: n(r.minRest24h, 10),
      maxWork24h: n(r.maxWork24h, 14),
      minRest7d: n(r.minRest7d, 77),
      maxRestSplits: n(r.maxRestSplits, 2),
      minLongestRestSegment: n(r.minLongestRestSegment, 6),
      maxGapBetweenRest: n(r.maxGapBetweenRest, 14),
    }),
    [r],
  );
  const examples = useMemo(() => buildComplianceExamples(thresholds), [thresholds]);

  const rows: Array<{ label: string; value: string; help: string }> = [
    {
      label: 'Min rest / 24h',
      value: `${thresholds.minRest24h} h`,
      help: 'Minimum hours of rest in any sliding 24-hour period',
    },
    {
      label: 'Max work / 24h',
      value: `${thresholds.maxWork24h} h`,
      help: 'Maximum hours of work in any sliding 24-hour period',
    },
    {
      label: 'Min rest / 7d',
      value: `${thresholds.minRest7d} h`,
      help: 'Minimum hours of rest in any 7-day period',
    },
    {
      label: 'Max rest splits',
      value: String(thresholds.maxRestSplits),
      help: 'Maximum number of rest periods in any 24h window',
    },
    {
      label: 'Min longest rest',
      value: `${thresholds.minLongestRestSegment} h`,
      help: 'One continuous rest block must reach at least this length',
    },
    {
      label: 'Max gap between rest',
      value: `${thresholds.maxGapBetweenRest} h`,
      help: 'Maximum hours between consecutive rest periods',
    },
  ];

  return (
    <ReportPack>
      <ReportPage size="portrait">
        <ReportHeader>
          <ReportTitleRow
            title="Working / rest hours — rule set"
            formId="MLC / STCW"
            logoSrc={companyLogoSrc}
            logoAlt={data.managementCompany?.name || 'Management company logo'}
          />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Rule set" value={r.name} />
              <ReportField label="Status" value={r.isActive ? 'Default (active)' : 'Not default'} />
              <ReportField
                label="Company"
                value={data.managementCompany?.name || '—'}
              />
            </ReportMetaRow>
          </ReportMeta>
          {r.notes?.trim() ? (
            <p className="ui-report-legal">{r.notes.trim()}</p>
          ) : (
            <p className="ui-report-legal">
              Thresholds used by the WRH month sheet, compliance checks, and weekly reports. Hours are
              evaluated in sliding windows (not calendar midnight). Overnight rest before the morning
              watch (e.g. 20:00–08:00) counts as one continuous block.
            </p>
          )}
        </ReportHeader>

        <ReportBody>
          <ReportSection title="Thresholds">
            <table className="wrh-rules-print-table">
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Value</th>
                  <th>Meaning</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td className="wrh-rules-print-value">{row.value}</td>
                    <td>{row.help}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ReportSection>

          <ReportSection title="Compliance examples">
            <div className="wrh-rules-print-examples">
              {examples.map((ex) => (
                <div
                  key={ex.title}
                  className={ex.ok ? 'wrh-rules-print-ex ok' : 'wrh-rules-print-ex nc'}
                >
                  <div className="wrh-rules-print-ex-head">
                    <span className="wrh-rules-print-badge">{ex.ok ? 'Compliant' : 'Non-compliant'}</span>
                    <strong>{ex.title.replace(/^Compliant — |^Non-compliant — /, '')}</strong>
                  </div>
                  <div className="wrh-rules-print-ex-sched">{ex.schedule}</div>
                  <ul>
                    {ex.points.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </ReportSection>
        </ReportBody>

        <ReportFooter>
          <ReportNote>
            Print date: {new Date().toLocaleString('en-GB')}. Only the default rule set drives live
            compliance on the month sheet.
          </ReportNote>
        </ReportFooter>
      </ReportPage>
    </ReportPack>
  );
};

export default RulesReport;
